import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { collectionEvidenceFilingSchema } from "./file-collection-evidence.step";

/**
 * Send Collection Filing Receipt Step
 *
 * Replies to the rep's forwarded email (same conversation, from the intake
 * mailbox that received it) confirming what was filed — or explaining what
 * could not be resolved (e.g. "Couldn't find COL-46").
 *
 * Never replies to unauthorized senders: when the sender did not resolve to
 * an org user the step is a silent no-op, so external/spoofed senders get no
 * signal that the intake mailbox does anything special.
 */
export const sendCollectionFilingReceiptStep = defineStepConfig({
  type: "send_collection_filing_receipt",
  name: "Send Collection Filing Receipt",
  description:
    "Replies to the rep's forward confirming the evidence filing outcome",
  category: "Integration",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The rep's forwarded email to reply to",
    ),
    authorized: z
      .boolean()
      .describe("Whether the sender resolved to an org user"),
    filings: z
      .array(collectionEvidenceFilingSchema)
      .describe("Per-thread filing outcomes"),
    unresolvedRefs: z
      .array(z.number().int().positive())
      .describe("Reference numbers that did not match any thread"),
  }),

  outputSchema: z.object({
    sent: z.boolean().describe("True when a receipt reply was dispatched"),
    skippedReason: z
      .string()
      .nullable()
      .describe(
        "Why no receipt was sent (e.g. unauthorized sender), null when sent",
      ),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
